import { createRef } from "react"
import { line } from "d3-shape"
import { WIRED_NAV_BAR } from "../data/uniqueValues"

const lineGenerator = line()

// Returns the center points of the answer box
export const getCoordinate = (ref, topDifference) => {
  const elem = ref?.current
  if (elem) {
    const box = elem.getBoundingClientRect()
    const { top, bottom, left, right, height, width } = {
      top: box.top + window.pageYOffset - topDifference,
      right: box.right + window.pageXOffset,
      bottom: box.bottom + window.pageYOffset - topDifference,
      left: box.left + window.pageXOffset,
      width: box.width,
      height: box.height,
    }
    const coords = {
      topMid: [left + width / 2, top],
      bottomMid: [left + width / 2, bottom],
      leftMid: [left, top + height / 2],
      rightMid: [right, top + height / 2],
      center: [left + width / 2, top + height / 2],
      topRight: [right, top],
      topLeft: [left, top],
      bottomRight: [right, bottom],
      bottomLeft: [left, bottom],
    }
    return coords
  }
}

// line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
// Determine the intersection point of two line segments
const intersect = (x1, y1, x2, y2, x3, y3, x4, y4) => {
  // Check if none of the lines are of length 0
  if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
    return false
  }
  let denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1)
  // Lines are parallel
  if (denominator === 0) {
    return false
  }
  let ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator
  let ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator
  // is the intersection along the segments
  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    return false
  }
  // Return a object with the x and y coordinates of the intersection
  let x = x1 + ua * (x2 - x1)
  let y = y1 + ua * (y2 - y1)
  return [x, y]
}

const rightPadding = 100,
  leftPadding = 100,
  headingPadding = 50,
  rightPadding2 = 10,
  leftPadding2 = 10,
  rightPadding3 = 5,
  leftPadding3 = 5

export const getPathFromHeadphoneToStart = (startRef, headphoneRef, svgRef) => {
  // Headphone image
  const p00 = getCoordinate(headphoneRef, WIRED_NAV_BAR)
  const point00 = [p00.bottomRight[0] - 3, p00.bottomRight[1] - 100]

  // Start button
  const p1 = getCoordinate(startRef, WIRED_NAV_BAR)
  const point02 = p1.leftMid

  const point01 = intersect(
    point00[0],
    point00[1],
    point00[0],
    point00[1] + 800,
    p1.leftMid[0],
    p1.leftMid[1],
    p1.leftMid[0] - 800,
    p1.leftMid[1]
  )

  const path = lineGenerator([point00, point01, point02])
  const pathLength = findPathLength(path, svgRef)

  return getPathAttributes(path, pathLength)
}

export const getPathsFromStartToQ1 = (
  questionnaire,
  headerCardRef,
  startRef,
  svgRef
) => {
  // Start button
  const p1 = getCoordinate(startRef, WIRED_NAV_BAR)
  const point1 = p1.rightMid

  // Header card
  const p2 = getCoordinate(headerCardRef, WIRED_NAV_BAR)
  const point2Temp = intersect(
    p2.topRight[0],
    p2.topRight[1],
    p2.bottomRight[0],
    p2.bottomRight[1],
    p1.rightMid[0] - 100000,
    p1.rightMid[1],
    p1.rightMid[0] + 100000,
    p1.rightMid[1]
  )

  let point2 = [point2Temp[0], point2Temp[1]]

  const lastQ = questionnaire[0]
  const { headingRef, qRef: card2Ref, vizRef } = lastQ

  const card2Points = getCoordinate(card2Ref, WIRED_NAV_BAR)
  const vizPoints = getCoordinate(vizRef, WIRED_NAV_BAR)

  let point3 = [card2Points.topRight[0], card2Points.topRight[1]]

  /* High padding makes the line inverted in smaller width devices.
   * Start button is always smaller than the music viz, so just check viz points.
   * point2 & point3 X coords should be always > music viz's X coord */
  if (card2Points.topRight[0] - rightPadding > vizPoints.rightMid[0]) {
    point2 = [point2Temp[0] - rightPadding, point2Temp[1]]
    point3 = [card2Points.topRight[0] - rightPadding, card2Points.topRight[1]]
  } else if (card2Points.topRight[0] - rightPadding2 > vizPoints.rightMid[0]) {
    point2 = [point2Temp[0] - rightPadding2, point2Temp[1]]
    point3 = [card2Points.topRight[0] - rightPadding2, card2Points.topRight[1]]
  } else {
    point2 = [point2Temp[0] - rightPadding3, point2Temp[1]]
    point3 = [card2Points.topRight[0] - rightPadding3, card2Points.topRight[1]]
  }

  let point4 = vizPoints.rightMid

  // Line 2
  const point5 = vizPoints.leftMid
  // const point6 = [card2Points.topLeft[0] + leftPadding, card2Points.topLeft[1]]
  let point6 = [card2Points.topLeft[0], card2Points.topLeft[1]]

  const p8 = getCoordinate(headingRef, WIRED_NAV_BAR)
  let point8 = [p8.leftMid[0] - headingPadding, p8.leftMid[1]]

  // const point7 = intersect(
  //   card2Points.topLeft[0] + leftPadding,
  //   card2Points.topLeft[1],
  //   card2Points.bottomLeft[0] + leftPadding,
  //   card2Points.bottomLeft[1],
  //   p8.leftMid[0],
  //   p8.leftMid[1],
  //   p8.leftMid[0] - 800,
  //   p8.leftMid[1]
  // )
  let point7 = intersect(
    card2Points.topLeft[0],
    card2Points.topLeft[1],
    card2Points.bottomLeft[0],
    card2Points.bottomLeft[1],
    p8.leftMid[0] + 100000,
    p8.leftMid[1],
    p8.leftMid[0] - 100000,
    p8.leftMid[1]
  )

  /* High padding makes the line inverted in smaller width devices.
   * Viz's left side & the Q's left side needs to be checked.
   * point6 & point7 X coords should be always <= Viz's & Q's X coords */
  if (
    point6[0] + leftPadding <= point5[0] &&
    point6[0] + leftPadding <= point8[0] &&
    point7[0] + leftPadding <= point5[0] &&
    point7[0] + leftPadding <= point8[0]
  ) {
    point6 = [point6[0] + leftPadding, point6[1]]
    point7 = [point7[0] + leftPadding, point7[1]]
  } else if (
    point6[0] + leftPadding2 <= point5[0] &&
    point6[0] + leftPadding2 <= point8[0] &&
    point7[0] + leftPadding2 <= point5[0] &&
    point7[0] + leftPadding2 <= point8[0]
  ) {
    point6 = [point6[0] + leftPadding2, point6[1]]
    point7 = [point7[0] + leftPadding2, point7[1]]
  } else {
    point6 = [point6[0] + leftPadding3, point6[1]]
    point7 = [point7[0] + leftPadding3, point7[1]]
  }

  // To prevent the left line from going the other direction
  // because of header padding
  if (point8[0] < point7[0]) {
    point8 = [...point7]
  }

  // To prevent the right line from going the other direction
  // because of header padding
  if (point4[0] > point3[0]) {
    point4 = [...point3]
  }

  const paths = [
    lineGenerator([point1, point2, point3, point4]),
    lineGenerator([point5, point6, point7, point8]),
  ]

  return paths.map((d) => getPathAttributes(d, findPathLength(d, svgRef)))
}

export const getPathsBetweenQuestions = (questionnaire, svgRef) => {
  // LINE 1
  const currentQ = questionnaire[questionnaire.length - 2]
  const { headingRef: heading1Ref, qRef: card1Ref } = currentQ

  const heading1Points = getCoordinate(heading1Ref, WIRED_NAV_BAR)
  const card1Points = getCoordinate(card1Ref, WIRED_NAV_BAR)

  let point1 = [
    heading1Points.rightMid[0] + headingPadding,
    heading1Points.rightMid[1],
  ]

  const point2Temp = intersect(
    card1Points.topRight[0],
    card1Points.topRight[1],
    card1Points.bottomRight[0],
    card1Points.bottomRight[1],
    point1[0] - 100000,
    point1[1],
    point1[0] + 100000,
    point1[1]
  )

  let point2 = [point2Temp[0] - rightPadding, point2Temp[1]]

  // To prevent the right line from going the other direction
  // because of header padding
  if (point2[0] < point1[0]) {
    point2 = [...point1]
  }

  const nextQ = questionnaire[questionnaire.length - 1]
  const { headingRef: heading2Ref, qRef: card2Ref, vizRef: viz2Ref } = nextQ

  const heading2Points = getCoordinate(heading2Ref, WIRED_NAV_BAR)
  const card2Points = getCoordinate(card2Ref, WIRED_NAV_BAR)

  let point3 = [point2[0], card2Points.topRight[1]]
  // let point3 = [point2[0] - rightPadding, card2Points.topRight[1]]

  let point6 = [card2Points.topLeft[0], card2Points.topLeft[1]]
  let point8 = [
    heading2Points.leftMid[0] - headingPadding,
    heading2Points.leftMid[1],
  ]

  let point7 = intersect(
    card2Points.topLeft[0],
    card2Points.topLeft[1],
    card2Points.bottomLeft[0],
    card2Points.bottomLeft[1],
    heading2Points.leftMid[0] + 100000,
    heading2Points.leftMid[1],
    heading2Points.leftMid[0] - 100000,
    heading2Points.leftMid[1]
  )

  // One single line, there will be no viz
  if (nextQ.loading) {
    point8 = [heading2Points.leftMid[0], heading2Points.leftMid[1]]
    /* High padding makes the line inverted in smaller width devices.
     * point6 & point7 X coords should be always <= Headphone jack's X coords */
    if (
      point6[0] + leftPadding <= point8[0] &&
      point7[0] + leftPadding <= point8[0]
    ) {
      point6 = [point6[0] + leftPadding, point6[1]]
      point7 = [point7[0] + leftPadding, point7[1]]
    } else if (
      point6[0] + leftPadding2 <= point8[0] &&
      point7[0] + leftPadding2 <= point8[0]
    ) {
      point6 = [point6[0] + leftPadding2, point6[1]]
      point7 = [point7[0] + leftPadding2, point7[1]]
    } else {
      point6 = [point6[0] + leftPadding3, point6[1]]
      point7 = [point7[0] + leftPadding3, point7[1]]
    }

    const paths = [
      lineGenerator([point1, point2, point3, point6, point7, point8]),
    ]
    return paths.map((d) => getPathAttributes(d, findPathLength(d, svgRef)))
  }

  const viz2Points = getCoordinate(viz2Ref, WIRED_NAV_BAR)

  let point4 = viz2Points.rightMid
  const point5 = viz2Points.leftMid

  // High padding makes the line inverted in smaller width devices,
  // fix for that
  if (
    card2Points.topRight[0] - rightPadding > viz2Points.rightMid[0] &&
    point2Temp[0] - rightPadding > point1[0]
  ) {
    point2 = [point2Temp[0] - rightPadding, point2Temp[1]]
    point3 = [point2[0], card2Points.topRight[1]]
    // point3 = [point2Temp[0] - rightPadding, point2Temp[1]]
  } else if (
    card2Points.topRight[0] - rightPadding2 > viz2Points.rightMid[0] &&
    point2Temp[0] - rightPadding2 > point1[0]
  ) {
    point2 = [point2Temp[0] - rightPadding2, point2Temp[1]]
    point3 = [point2[0], card2Points.topRight[1]]
  } else {
    point2 = [point2Temp[0] - rightPadding3, point2Temp[1]]
    point3 = [point2[0], card2Points.topRight[1]]
  }

  /* High padding makes the line inverted in smaller width devices.
   * Viz's left side & the Q's left side needs to be checked.
   * point6 & point7 X coords should be always <= Viz's & Q's X coords */
  if (
    point6[0] + leftPadding <= point5[0] &&
    point6[0] + leftPadding <= point8[0] &&
    point7[0] + leftPadding <= point5[0] &&
    point7[0] + leftPadding <= point8[0]
  ) {
    point6 = [point6[0] + leftPadding, point6[1]]
    point7 = [point7[0] + leftPadding, point7[1]]
  } else if (
    point6[0] + leftPadding2 <= point5[0] &&
    point6[0] + leftPadding2 <= point8[0] &&
    point7[0] + leftPadding2 <= point5[0] &&
    point7[0] + leftPadding2 <= point8[0]
  ) {
    point6 = [point6[0] + leftPadding2, point6[1]]
    point7 = [point7[0] + leftPadding2, point7[1]]
  } else {
    point6 = [point6[0] + leftPadding3, point6[1]]
    point7 = [point7[0] + leftPadding3, point7[1]]
  }

  // To prevent the left line from going the other direction
  // because of header padding
  if (point8[0] < point7[0]) {
    point8 = [...point7]
  }

  // To prevent the right line from going the other direction
  // because of header padding
  if (point4[0] > point3[0]) {
    point4 = [...point3]
  }

  const paths = [
    lineGenerator([point1, point2, point3, point4]),
    lineGenerator([point5, point6, point7, point8]),
  ]
  return paths.map((d) => getPathAttributes(d, findPathLength(d, svgRef)))
}

export const findPathLength = (d, svgRef) => {
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
  path.setAttribute("id", "dummyPathNodeWithUniqueId")
  path.setAttribute("d", d)
  path.style.display = "none"

  svgRef.current.appendChild(path)

  const insertedPath = document.getElementById("dummyPathNodeWithUniqueId")
  const length = insertedPath.getTotalLength()
  insertedPath.remove()
  return length
}

export const getPathAttributes = (d, length) => ({
  d,
  strokeDasharray: `${length} ${length}`,
  strokeDashoffset: length * 2,
  ref: createRef(),
})
